import React, { useState, useEffect } from 'react';
import { Wind, Navigation, Battery } from 'lucide-react';
import { Device } from '../../types';
import deviceService from '../../services/deviceService';
import {
  SensorPanelsContainer,
  LeftColumn,
  RightColumn,
  SensorPanel,
  PanelHeader,
  PanelIcon,
  PanelTitle,
  SensorList,
  SensorRow,
  SensorLabel,
  SensorValue
} from '../../styles/SensorPanelStyles';

interface NetatmoWindDetailsProps {
  device: Device;
  deviceId: string;
}

interface NetatmoData {
  wind_strength?: number;
  wind_angle?: number;
  gust_strength?: number;
  gust_angle?: number;
  battery_percent?: number;
  rf_status?: number;
  last_seen?: string;
}

const NetatmoWindDetails: React.FC<NetatmoWindDetailsProps> = ({ device, deviceId }) => {
  const [latestData, setLatestData] = useState<NetatmoData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLatestData = async () => {
      try {
        const data = await deviceService.getLatestData(deviceId);
        if (data && data.decoded_json) {
          setLatestData(data.decoded_json);
        }
      } catch (error) {
        console.error('Error fetching latest data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestData();
  }, [deviceId]);

  const formatWindSpeed = (speed: number | undefined): string => {
    return speed !== undefined ? `${speed.toFixed(1)} km/h` : 'N/A';
  };

  const formatBattery = (battery: number | undefined): string => {
    return battery !== undefined ? `${battery.toFixed(0)}%` : 'N/A';
  };

  const getCardinalDirection = (angle: number | undefined): string => {
    if (angle === undefined) return 'N/A';
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const index = Math.round(angle / 22.5) % 16;
    return directions[index];
  };

  const getBeaufortScale = (speed: number | undefined): { scale: number; description: string; status: 'normal' | 'warning' | 'critical' } => {
    if (speed === undefined) return { scale: 0, description: 'Unknown', status: 'normal' };
    if (speed < 1) return { scale: 0, description: 'Calm', status: 'normal' };
    if (speed < 6) return { scale: 1, description: 'Light Air', status: 'normal' };
    if (speed < 12) return { scale: 2, description: 'Light Breeze', status: 'normal' };
    if (speed < 20) return { scale: 3, description: 'Gentle Breeze', status: 'normal' };
    if (speed < 29) return { scale: 4, description: 'Moderate Breeze', status: 'normal' };
    if (speed < 39) return { scale: 5, description: 'Fresh Breeze', status: 'warning' };
    if (speed < 50) return { scale: 6, description: 'Strong Breeze', status: 'warning' };
    if (speed < 62) return { scale: 7, description: 'Near Gale', status: 'critical' };
    if (speed < 75) return { scale: 8, description: 'Gale', status: 'critical' };
    if (speed < 89) return { scale: 9, description: 'Strong Gale', status: 'critical' };
    if (speed < 103) return { scale: 10, description: 'Storm', status: 'critical' };
    if (speed < 118) return { scale: 11, description: 'Violent Storm', status: 'critical' };
    return { scale: 12, description: 'Hurricane', status: 'critical' };
  };

  const getBatteryStatus = (battery: number | undefined): 'normal' | 'warning' | 'critical' => {
    if (battery === undefined) return 'normal';
    if (battery > 60) return 'normal';
    if (battery > 20) return 'warning';
    return 'critical';
  };

  const getSignalQuality = (rfStatus: number | undefined): string => {
    if (rfStatus === undefined) return 'Unknown';
    if (rfStatus > 80) return 'Excellent';
    if (rfStatus > 60) return 'Good';
    if (rfStatus > 40) return 'Fair';
    return 'Weak';
  };

  const beaufortScale = getBeaufortScale(latestData?.wind_strength);
  const gustBeaufort = getBeaufortScale(latestData?.gust_strength);

  if (loading) {
    return (
      <SensorPanelsContainer>
        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
          Loading wind gauge data...
        </div>
      </SensorPanelsContainer>
    );
  }

  return (
    <SensorPanelsContainer>
      <LeftColumn>
        <SensorPanel>
          <PanelHeader>
            <PanelIcon>
              <Wind size={16} />
            </PanelIcon>
            <PanelTitle>Wind Data</PanelTitle>
          </PanelHeader>
          <SensorList>
            <SensorRow>
              <SensorLabel>Wind Speed</SensorLabel>
              <SensorValue status={beaufortScale.status}>{formatWindSpeed(latestData?.wind_strength)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Wind Direction</SensorLabel>
              <SensorValue status="normal">
                {getCardinalDirection(latestData?.wind_angle)} ({latestData?.wind_angle !== undefined ? `${latestData.wind_angle.toFixed(0)}°` : 'N/A'})
              </SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Beaufort Scale</SensorLabel>
              <SensorValue status={beaufortScale.status}>{beaufortScale.scale} - {beaufortScale.description}</SensorValue>
            </SensorRow>
          </SensorList>
        </SensorPanel>

        <SensorPanel>
          <PanelHeader>
            <PanelIcon>
              <Navigation size={16} />
            </PanelIcon>
            <PanelTitle>Wind Gusts</PanelTitle>
          </PanelHeader>
          <SensorList>
            <SensorRow>
              <SensorLabel>Gust Speed</SensorLabel>
              <SensorValue status={gustBeaufort.status}>{formatWindSpeed(latestData?.gust_strength)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Gust Direction</SensorLabel>
              <SensorValue status="normal">
                {getCardinalDirection(latestData?.gust_angle)} ({latestData?.gust_angle !== undefined ? `${latestData.gust_angle.toFixed(0)}°` : 'N/A'})
              </SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Gust Intensity</SensorLabel>
              <SensorValue status={gustBeaufort.status}>{gustBeaufort.description}</SensorValue>
            </SensorRow>
          </SensorList>
        </SensorPanel>
      </LeftColumn>

      <RightColumn>
        <SensorPanel>
          <PanelHeader>
            <PanelIcon>
              <Battery size={16} />
            </PanelIcon>
            <PanelTitle>Device Health</PanelTitle>
          </PanelHeader>
          <SensorList>
            <SensorRow>
              <SensorLabel>Battery Level</SensorLabel>
              <SensorValue status={getBatteryStatus(latestData?.battery_percent)}>{formatBattery(latestData?.battery_percent)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Battery Status</SensorLabel>
              <SensorValue status={getBatteryStatus(latestData?.battery_percent)}>
                {latestData?.battery_percent === undefined ? 'Unknown' :
                 latestData.battery_percent > 60 ? 'Good' :
                 latestData.battery_percent > 20 ? 'Low' :
                 'Critical'}
              </SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Signal Quality</SensorLabel>
              <SensorValue status="normal">{getSignalQuality(latestData?.rf_status)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Last Seen</SensorLabel>
              <SensorValue status="normal">
                {latestData?.last_seen ? new Date(latestData.last_seen).toLocaleString() : 'N/A'}
              </SensorValue>
            </SensorRow>
          </SensorList>
        </SensorPanel>

        <SensorPanel>
          <PanelHeader>
            <PanelIcon>
              <Wind size={16} />
            </PanelIcon>
            <PanelTitle>Beaufort Scale Reference</PanelTitle>
          </PanelHeader>
          <SensorList>
            <SensorRow>
              <SensorLabel>0-1: Calm/Light Air</SensorLabel>
              <SensorValue status="normal">&lt; 6 km/h</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>2-3: Light/Gentle Breeze</SensorLabel>
              <SensorValue status="normal">6-20 km/h</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>4-5: Moderate/Fresh Breeze</SensorLabel>
              <SensorValue status="normal">20-39 km/h</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>6-7: Strong Breeze/Gale</SensorLabel>
              <SensorValue status="warning">39-62 km/h</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>8+: Gale/Storm</SensorLabel>
              <SensorValue status="critical">&gt; 62 km/h</SensorValue>
            </SensorRow>
          </SensorList>
        </SensorPanel>
      </RightColumn>
    </SensorPanelsContainer>
  );
};

export default NetatmoWindDetails;
